import {
  collection,
  type DocumentData,
  DocumentReference,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  type Query,
  type QueryDocumentSnapshot,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { PERSISTENT_STORAGE, SESSION_STORAGE } from "../constants/storageKeys";
import { useUser } from "../context/userContext";
import type { Generation } from "../types";

interface LibraryItem extends Generation {}

const useFetchLibrary = () => {
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [errorInLibrary, setError] = useState<unknown | null>(null);
  const isFetchingRef = useRef(false);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const lastVisible = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const { userId } = useUser();
  const firestore = getFirestore();

  // Get identifiers from storage
  const organizationId = localStorage.getItem(
    PERSISTENT_STORAGE.ORGANIZATION_ID,
  );
  const projectId = localStorage.getItem(PERSISTENT_STORAGE.PROJECT_ID);
  const sessionId = sessionStorage.getItem(SESSION_STORAGE.SESSION_ID);

  // Debug logging
  useEffect(() => {
    console.log("useFetchLibrary: identifiers =", {
      organizationId,
      projectId,
      sessionId,
    });
  }, [organizationId, projectId, sessionId]);

  // Reset library when session changes
  useEffect(() => {
    console.log("useFetchLibrary: session changed, resetting library");
    setLibrary([]);
    lastVisible.current = null;
    setHasMorePages(true);
  }, [sessionId]);

  // Function to refresh the library data
  const refreshLibrary = useCallback(async () => {
    if (!organizationId || !projectId || !sessionId) return;

    setLoadingLibrary(true);
    lastVisible.current = null; // Reset pagination

    try {
      const generationsCollection = collection(
        firestore,
        `organizations/${organizationId}/projects/${projectId}/sessions/${sessionId}/generations`,
      );
      const q = query(
        generationsCollection,
        orderBy("createdAt", "desc"),
        limit(4),
      );

      const querySnapshot = await getDocs(q);
      const newGenerations = querySnapshot.docs.map(
        (doc) =>
          ({
            ...doc.data(),
            id: doc.id,
          }) as Generation,
      );
      setLibrary(newGenerations);

      lastVisible.current = querySnapshot.docs[querySnapshot.docs.length - 1];
      setHasMorePages(querySnapshot.docs.length === 4);
    } catch (error) {
      console.error("Error refreshing library:", error);
      setError(error);
    } finally {
      setLoadingLibrary(false);
    }
  }, [organizationId, projectId, sessionId]);

  const fetchLibrary = useCallback(async () => {
    const itemsPerPage = 4;
    console.log("fetchLibrary called:", {
      organizationId,
      projectId,
      sessionId,
      isFetching: isFetchingRef.current,
      hasMorePages,
    });

    if (
      !organizationId ||
      !projectId ||
      !sessionId ||
      isFetchingRef.current ||
      !hasMorePages
    ) {
      console.log("fetchLibrary: early return - conditions not met:", {
        organizationId: !!organizationId,
        projectId: !!projectId,
        sessionId: !!sessionId,
        isFetching: isFetchingRef.current,
        hasMorePages,
      });
      return;
    }

    isFetchingRef.current = true;
    setLoadingLibrary(true);

    try {
      const generationsCollection = collection(
        firestore,
        `organizations/${organizationId}/projects/${projectId}/sessions/${sessionId}/generations`,
      );
      let q = query(
        generationsCollection,
        orderBy("createdAt", "desc"),
        limit(itemsPerPage + 1),
      );

      if (lastVisible.current) {
        q = query(q, startAfter(lastVisible.current));
      }

      console.log("fetchLibrary: executing query for session:", sessionId);
      const querySnapshot = await getDocs(q);
      console.log(
        "fetchLibrary: query returned",
        querySnapshot.docs.length,
        "documents",
      );

      const newGenerations = querySnapshot.docs.slice(0, itemsPerPage).map(
        (doc) =>
          ({
            ...doc.data(),
            id: doc.id,
          }) as Generation,
      );

      setLibrary((prevGenerations) => [...prevGenerations, ...newGenerations]);

      lastVisible.current =
        querySnapshot.docs[
          Math.min(querySnapshot.docs.length, itemsPerPage) - 1
        ];
      setHasMorePages(querySnapshot.docs.length > itemsPerPage);
      console.log(
        "fetchLibrary: state updated with",
        newGenerations.length,
        "new generations",
      );
    } catch (error) {
      console.error("Error fetching library:", error);
      setError(error);
    } finally {
      setLoadingLibrary(false);
      isFetchingRef.current = false;
    }
  }, [organizationId, projectId, sessionId, hasMorePages]);

  // Set up real-time updates for new generations
  useEffect(() => {
    if (!organizationId || !projectId || !sessionId) return;

    const generationsCollection = collection(
      firestore,
      `organizations/${organizationId}/projects/${projectId}/sessions/${sessionId}/generations`,
    );
    const q = query(
      generationsCollection,
      orderBy("createdAt", "desc"),
      limit(1),
    ) as Query<DocumentData>;

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            const newGeneration = {
              ...change.doc.data(),
              id: change.doc.id,
            } as Generation;

            setLibrary((prevLibrary) => {
              // Check if the generation is already in the library
              const exists = prevLibrary.some(
                (item) => item.id === newGeneration.id,
              );
              if (exists) return prevLibrary;

              // Add the new generation at the beginning of the library
              return [newGeneration, ...prevLibrary];
            });
          }
        });
      },
      (error) => {
        console.error("Error in real-time updates:", error);
        setError(error);
      },
    );

    return () => unsubscribe();
  }, [organizationId, projectId, sessionId]);

  return {
    loadingLibrary,
    library,
    errorInLibrary,
    fetchLibrary,
    hasMorePages,
    refreshLibrary,
  };
};

export default useFetchLibrary;
